/**
 * Zugriff auf Inhaltssteuerelemente – die Bausteine von Word-Formularen.
 *
 * Verfügbar ab WordApi 1.1 und damit überall dort, wo das Add-in startet.
 * Word.js meldet allerdings nur die Typen, die es selbst unterstützt; ältere
 * Formularfelder aus der Entwicklertools-Gruppe "Vorversionstools" tauchen hier
 * nicht auf.
 */

import { isSetSupported } from "@/office/requirements";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface ContentControlInfo {
  index: number;
  /** "RichText", "PlainText", "CheckBox", "DropDownList", "ComboBox", "DatePicker" … */
  type: string;
  /** Beschriftung, die Word über dem Element anzeigt. */
  title: string;
  /** Frei vergebbare Kennung, über die sich Elemente später auswerten lassen. */
  tag: string;
  /** Aktueller Inhalt des Elements. */
  text: string;
}

export async function readContentControls(): Promise<ContentControlInfo[]> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load("items/type,items/title,items/tag,items/text");
    await context.sync();

    return controls.items.map((control, index) => ({
      index,
      type: typeof control.type === "string" ? control.type : "Unknown",
      title: typeof control.title === "string" ? control.title.trim() : "",
      tag: typeof control.tag === "string" ? control.tag.trim() : "",
      text: typeof control.text === "string" ? control.text.trim() : "",
    }));
  });
}

/** Alle Steuerelemente eines bestimmten Typs. */
export function controlsOfType(
  controls: ContentControlInfo[],
  ...types: string[]
): ContentControlInfo[] {
  const gesucht = types.map((type) => type.toLowerCase());
  return controls.filter((control) => gesucht.includes(control.type.toLowerCase()));
}

/** Wie viele verschiedene Typen kommen im Formular vor? */
export function distinctTypes(controls: ContentControlInfo[]): string[] {
  return [...new Set(controls.map((control) => control.type))].sort();
}

export interface AuswahllistenInfo {
  title: string;
  type: string;
  /** Anzahl der Einträge in der Liste. */
  eintraege: number;
}

/**
 * Dropdownlisten und Kombinationsfelder samt Zahl ihrer Einträge.
 * `null`, wenn WordApi 1.9 fehlt – erst dort sind die Listeneinträge lesbar.
 */
export async function readAuswahllisten(): Promise<AuswahllistenInfo[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!isSetSupported("WordApi", "1.9")) return null;

  try {
    return await Word.run(async (context) => {
      const controls = context.document.body.contentControls;
      controls.load("items/type,items/title");
      await context.sync();
      const listen = controls.items
        .filter((control) => control.type === "DropDownList" || control.type === "ComboBox")
        .map((control) => {
          const eintraege =
            control.type === "DropDownList"
              ? control.dropDownListContentControl.listItems
              : control.comboBoxContentControl.listItems;
          eintraege.load("items");
          return { control, eintraege };
        });
      await context.sync();
      return listen.map(({ control, eintraege }) => ({
        title: typeof control.title === "string" ? control.title : "",
        type: String(control.type),
        eintraege: eintraege.items.length,
      }));
    });
  } catch {
    return null;
  }
}
